import { request } from "../../../services/api"
import type { BaseApiResponse } from "../../../types/api.ts"
import type {
  BillableLinesResponse,
  GenerateInvoicePayload,
  InvoiceDelivery,
  InvoiceLinkedPayment,
  InvoiceListItem,
  InvoicesListResponse,
  InvoicesSummary,
  SendInvoiceResponse
} from "../types/invoices.types.ts"

const BASE_URL = `/project-management/invoices`

const PATHS = {
  getInvoices: `${BASE_URL}/`,
  getInvoicesSummary: `${BASE_URL}/summary/`,
  exportInvoices: `${BASE_URL}/export/`,
  generateInvoice: `${BASE_URL}/generate/`,
  getBillableLines: `${BASE_URL}/billable_lines/`,
  issueInvoice: (id: string) => `${BASE_URL}/${id}/issue/`,
  voidInvoice: (id: string) => `${BASE_URL}/${id}/void/`,
  sendInvoice: (id: string) => `${BASE_URL}/${id}/send/`,
  getInvoiceDeliveries: (id: string) => `${BASE_URL}/${id}/deliveries/`,
  getInvoicePdf: (id: string) => `${BASE_URL}/${id}/pdf/`,
  getInvoicePayments: (id: string) => `${BASE_URL}/${id}/payments/`,
  patchInvoice: (id: string) => `${BASE_URL}/${id}/`,
  deleteInvoice: (id: string) => `${BASE_URL}/${id}/`
}

export interface InvoicesListParams {
  page?: number
  page_size?: number
  q?: string
  customer?: string
  origin?: number
  status?: number
  date_issued_from?: string
  date_issued_to?: string
  due_date_from?: string
  due_date_to?: string
  ordering?: string
}

export const getInvoices = async (params: InvoicesListParams) => {
  return request.api.get<InvoicesListResponse, BaseApiResponse<unknown>>(
    PATHS.getInvoices,
    params
  )
}

/** Money totals over the whole filtered collection — the KPI row's source,
 * so it stops summing the paginated page. Takes the same filter params. */
export const getInvoicesSummary = async (
  params: Omit<InvoicesListParams, "page" | "page_size" | "ordering">
) => {
  return request.api.get<InvoicesSummary, BaseApiResponse<unknown>>(
    PATHS.getInvoicesSummary,
    params
  )
}

export const exportInvoices = async (params: InvoicesListParams) => {
  return request.api.get<Blob, BaseApiResponse<unknown>>(
    PATHS.exportInvoices,
    params,
    { responseType: "blob" }
  )
}

export const generateInvoice = async (data: GenerateInvoicePayload) => {
  return request.api.post<InvoiceListItem, BaseApiResponse<unknown>>(
    PATHS.generateInvoice,
    data
  )
}

export const getBillableLines = async (projectId: string) => {
  return request.api.get<BillableLinesResponse, BaseApiResponse<unknown>>(
    PATHS.getBillableLines,
    { project: projectId }
  )
}

export const issueInvoice = async (id: string) => {
  return request.api.post<InvoiceListItem, BaseApiResponse<unknown>>(
    PATHS.issueInvoice(id)
  )
}

export const voidInvoice = async (id: string) => {
  return request.api.post<InvoiceListItem, BaseApiResponse<unknown>>(
    PATHS.voidInvoice(id)
  )
}

export const sendInvoice = async (id: string, recipient?: string) => {
  return request.api.post<SendInvoiceResponse, BaseApiResponse<unknown>>(
    PATHS.sendInvoice(id),
    recipient ? { recipient } : {}
  )
}

export const getInvoiceDeliveries = async (id: string) => {
  return request.api.get<InvoiceDelivery[], BaseApiResponse<unknown>>(
    PATHS.getInvoiceDeliveries(id)
  )
}

export const getInvoicePayments = async (id: string) => {
  return request.api.get<InvoiceLinkedPayment[], BaseApiResponse<unknown>>(
    PATHS.getInvoicePayments(id)
  )
}

export const getInvoicePdf = async (id: string) => {
  return request.api.get<Blob, BaseApiResponse<unknown>>(
    PATHS.getInvoicePdf(id),
    {},
    { responseType: "blob" }
  )
}

export const patchGeneratedInvoice = async (
  id: string,
  data: Partial<GenerateInvoicePayload> & { status?: number }
) => {
  return request.api.patch<InvoiceListItem, BaseApiResponse<unknown>>(
    PATHS.patchInvoice(id),
    data
  )
}

export const deleteInvoice = async (id: string) => {
  return request.api.delete<BaseApiResponse<unknown>, BaseApiResponse<unknown>>(
    PATHS.deleteInvoice(id)
  )
}
